import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, UserPlus, GraduationCap, CreditCard, Calendar, Building } from 'lucide-react';

export type ImportType = 'students' | 'families' | 'classes' | 'enrollments' | 'payments' | 'staff' | 'family-complete';

interface ImportOption {
  id: ImportType;
  title: string;
  description: string;
  icon: React.ReactNode;
  examples: string[];
  difficulty: 'easy' | 'medium' | 'advanced';
  estimatedTime: string;
}

const importOptions: ImportOption[] = [
  {
    id: 'students',
    title: 'Students',
    description: 'Import student profiles with basic information',
    icon: <Users className="w-8 h-8" />,
    examples: ['First Name', 'Last Name', 'Date of Birth', 'Parent Email'],
    difficulty: 'easy',
    estimatedTime: '5-10 minutes'
  },
  {
    id: 'families',
    title: 'Families',
    description: 'Import family units with parent and emergency contact information',
    icon: <UserPlus className="w-8 h-8" />,
    examples: ['Family Name', 'Parent Names', 'Email', 'Phone', 'Address'],
    difficulty: 'medium',
    estimatedTime: '10-15 minutes'
  },
  {
    id: 'classes',
    title: 'Classes & Programs',
    description: 'Import class schedules, capacity, and instructor assignments',
    icon: <GraduationCap className="w-8 h-8" />,
    examples: ['Class Name', 'Instructor', 'Schedule', 'Capacity', 'Age Group'],
    difficulty: 'easy',
    estimatedTime: '5-10 minutes'
  },
  {
    id: 'enrollments',
    title: 'Enrollments',
    description: 'Import student-to-class enrollments and registrations',
    icon: <Calendar className="w-8 h-8" />,
    examples: ['Student Name', 'Class Name', 'Enrollment Date', 'Status'],
    difficulty: 'advanced',
    estimatedTime: '15-20 minutes'
  },
  {
    id: 'payments',
    title: 'Payment History',
    description: 'Import billing history and payment records',
    icon: <CreditCard className="w-8 h-8" />,
    examples: ['Family', 'Amount', 'Date', 'Payment Method', 'Status'],
    difficulty: 'advanced',
    estimatedTime: '15-25 minutes'
  },
  {
    id: 'staff',
    title: 'Staff & Instructors',
    description: 'Import instructor profiles and staff information',
    icon: <Building className="w-8 h-8" />,
    examples: ['Name', 'Email', 'Phone', 'Specialties', 'Availability'],
    difficulty: 'medium',
    estimatedTime: '10-15 minutes'
  }
];

interface ImportTypeSelectorProps {
  onSelectType: (type: ImportType) => void;
  selectedType?: ImportType;
}

export function ImportTypeSelector({ onSelectType, selectedType }: ImportTypeSelectorProps) {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'advanced': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">What would you like to import?</h2>
        <p className="text-gray-600 mt-2">
          Choose the type of data you're importing. We'll guide you through the process step by step.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {importOptions.map((option) => (
          <Card 
            key={option.id}
            className={`cursor-pointer transition-all hover:shadow-lg ${
              selectedType === option.id 
                ? 'ring-2 ring-blue-500 shadow-lg' 
                : 'hover:ring-1 hover:ring-gray-300'
            }`}
            onClick={() => onSelectType(option.id)}
          >
            <CardHeader className="text-center">
              <div className="flex justify-center mb-3">
                <div className={`p-3 rounded-full ${
                  selectedType === option.id 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {option.icon}
                </div>
              </div>
              <CardTitle className="text-lg">{option.title}</CardTitle>
              <div className="flex justify-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(option.difficulty)}`}>
                  {option.difficulty}
                </span>
                <span className="text-xs text-gray-500">{option.estimatedTime}</span>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">{option.description}</p>
              
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Expected columns:</p>
                <div className="flex flex-wrap gap-1">
                  {option.examples.map((example, index) => (
                    <span 
                      key={index}
                      className="inline-block px-2 py-1 bg-gray-100 text-xs rounded text-gray-600"
                    >
                      {example}
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedType && (
        <div className="flex justify-center">
          <Button 
            size="lg"
            onClick={() => {}}
            className="px-8"
          >
            Continue with {importOptions.find(opt => opt.id === selectedType)?.title}
          </Button>
        </div>
      )}

      <div className="mt-8 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm font-medium">💡</span>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-blue-900">Pro Tips for Success</h4>
            <ul className="mt-2 text-sm text-blue-700 space-y-1">
              <li>• Start with <strong>Students</strong> if this is your first import</li>
              <li>• Make sure your CSV file has headers in the first row</li>
              <li>• We'll help you map any column names that don't match exactly</li>
              <li>• You can always import more data types later</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}